import { ScrollView, StyleSheet, Text, View } from 'react-native';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import ReservationCard from 'src/presentation/components/organisms/ReservationCard';
import AddReservationCard from 'src/presentation/components/organisms/AddReservationCard';
import AddReservationManuallyDialog from 'src/presentation/dialogs/AddReservationManuallyDialog';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import { useReservationsStore } from 'src/domain/stores/useReservationsStore';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import { StatusBar } from 'expo-status-bar';
import Colors from 'src/core/utils/Colors';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import type ApiReservation from 'src/core/models/api/ApiReservation';
import type { ReservationsScreenParams } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';

export default function ReservationsScreen() {
	const route = useRoute<RouteProp<{ params: ReservationsScreenParams | undefined }>>();
	const screenType = route.params?.screenType;

	const tabBarInset = useTabBarInset();
	const {
		upcomingReservations,
		historicalReservations,
		isLoadingReservations,
		hasReservationsError,
		isAddReservationManuallyDialogVisible,
		getApiReservations,
		setAddReservationManuallyDialogVisible,
	} = useReservationsStore();

	const reservations = screenType?.isPastOrder ? historicalReservations : upcomingReservations;

	const fetchData = async () => {
		if (!screenType) {
			return;
		}

		await getApiReservations({ screenType });
	};

	useAppStateScreenListener({
		onFocussed: () => fetchData(),
		onForegrounded: () => fetchData(),
	});

	if (!screenType) return null;

	return (
		<>
			<StatusBar style="dark" />
			<View style={styles.container}>
				<BackgroundPattern />
				<AddReservationManuallyDialog
					isDialogVisible={isAddReservationManuallyDialogVisible}
					onCancelled={() => setAddReservationManuallyDialogVisible(false)}
					onSuccess={async () => {
						setAddReservationManuallyDialogVisible(false);
						await fetchData();
					}}
				/>
				{isLoadingReservations ? (
					<View style={styles.loadingIndicatorContainer}>
						<LoadingIndicator />
						<Text style={[styles.bodyText, styles.loadingIndicatorText]}>{Translations.loadingReservationsText}</Text>
					</View>
				) : hasReservationsError ? (
					<ErrorView onPress={fetchData} />
				) : (
					<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }} style={styles.scrollView}>
						<AddReservationCard onPress={() => setAddReservationManuallyDialogVisible(true)} />
						{reservations?.data?.length ? (
							<View>
								{reservations.data?.map((item: ApiReservation) => (
									<ReservationCard
										arrivalDate={item.arrival_date}
										arrivalTime={item.arrival_time}
										canRebook={item.can_rebook}
										id={item.id}
										image={item.workspace.logo}
										key={item.id}
										label={item.name}
										pastOrders={screenType.isPastOrder}
										personCount={item.persons_count}
										reservationFlowUrl={screenType.getReservationFlowUrl(item)}
										workspaceTitle={item.workspace.title}
									/>
								))}
								{reservations.data.length >= Constants.reservationsPageSize ? (
									<View style={styles.maxItemsReachedContainer}>
										<Text style={[styles.bodyText, styles.maxItemsReachedText]}>
											{Translations.onlyTheMostRecentReservationsShownText}
										</Text>
									</View>
								) : null}
							</View>
						) : (
							<View style={styles.placeholderContainer}>
								<Text>{screenType.placeholder}</Text>
							</View>
						)}
						<View style={{ height: Dimens.size40 }} />
					</ScrollView>
				)}
			</View>
		</>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: Dimens.flex1,
		backgroundColor: Colors.gray50,
	},
	scrollView: {
		paddingTop: Dimens.padding20,
		paddingHorizontal: Dimens.padding18,
	},
	placeholderContainer: {
		alignSelf: 'center',
		justifyContent: 'center',
		marginVertical: Dimens.padding20,
	},
	bodyText: {
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
	},
	loadingIndicatorContainer: {
		flex: Dimens.flex1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	loadingIndicatorText: {
		marginTop: Dimens.padding20,
		paddingHorizontal: Dimens.padding40,
		textAlign: 'center',
	},
	maxItemsReachedContainer: {
		marginTop: Dimens.padding20,
		paddingHorizontal: Dimens.padding40,
		justifyContent: 'center',
	},
	maxItemsReachedText: {
		textAlign: 'center',
		color: Colors.gray600,
	},
});
